import React, { useState, useRef } from 'react';
import type { FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Checkbox as AkCheckbox } from '@atlaskit/checkbox';

import {
	SatisfactionSurveyComponent,
	type OnInteractionData,
} from '@confluence/satisfaction-survey';

const i18n = defineMessages({
	title: {
		id: 'external-collab-ui.satisfaction-survey.title',
		defaultMessage: 'Tell us what you think',
		description:
			'Title for External Collab Satisfaction Survey, shown on clicking a feedback button',
	},
	description: {
		id: 'external-collab-ui.satisfaction-survey.description',
		defaultMessage: 'How would you rate the guest experience?',
		description: 'The primary question for Single Space External Collab Satisfaction Survey',
	},
	finalTitle: {
		id: 'external-collab-ui.satisfaction-survey.final.title',
		defaultMessage: 'Feedback submitted',
		description: 'Final title for External Collab Satisfaction Survey after submission',
	},
	finalDescription: {
		id: 'external-collab-ui.satisfaction-survey.final.description',
		defaultMessage: 'Thanks for helping us make Confluence better!',
		description:
			'Acknowledgement text shown after the user submits the External Collab Satisfaction Survey',
	},
	canContact: {
		id: 'external-collab-ui.satisfaction-survey.checkbox.can-contact',
		defaultMessage: 'Atlassian can contact me about this feedback',
		description: 'Label for checkbox where users allow us to contact them for more feedback',
	},
	questionGood: {
		id: 'external-collab-ui.satisfaction-survey.question.good',
		defaultMessage:
			'Great! What do you like about it? Is there anything that could be improved? (optional)',
		description: 'Prompt for additional feedback when the user is happy',
	},
	questionNeutral: {
		id: 'external-collab-ui.satisfaction-survey.question.okay',
		defaultMessage:
			'Could you tell us a little more? What do you like about it? What could be improved? (optional)',
		description: 'Prompt for additional feedback when the user is neutral',
	},
	questionBad: {
		id: 'external-collab-ui.satisfaction-survey.question.bad',
		defaultMessage:
			// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
			// eslint-disable-next-line no-restricted-syntax
			"We're sorry to hear that. What hasn't been working for you? Is there anything you like about it? (optional)",
		description: 'Prompt for additional feedback when the user is unhappy',
	},
	reactionTerrible: {
		id: 'external-collab-ui.satisfaction-survey.reaction.terrible',
		defaultMessage: 'Terrible',
		description:
			"Tooltip text for the 'Terrible' reaction icon, indicating how the user feels about the External Collab feature",
	},
	reactionBad: {
		id: 'external-collab-ui.satisfaction-survey.reaction.bad',
		defaultMessage: 'Bad',
		description:
			"Tooltip text for the 'Bad' reaction icon, indicating how the user feels about the External Collab feature",
	},
	reactionOkay: {
		id: 'external-collab-ui.satisfaction-survey.reaction.okay',
		defaultMessage: 'Okay',
		description:
			"Tooltip text for the 'Okay' reaction icon, indicating how the user feels about the External Collab feature",
	},
	reactionGreat: {
		id: 'external-collab-ui.satisfaction-survey.reaction.great',
		defaultMessage: 'Great',
		description:
			"Tooltip text for the 'Great' reaction icon, indicating how the user feels about the External Collab feature",
	},
	reactionAmazing: {
		id: 'external-collab-ui.satisfaction-survey.reaction.amazing',
		defaultMessage: 'Amazing',
		description:
			"Tooltip text for the 'Amazing' reaction icon, indicating how the user feels about the External Collab feature",
	},
	buttonSubmit: {
		id: 'external-collab-ui.satisfaction-survey.button.submit',
		defaultMessage: 'Submit',
		description: 'Button to submit the survey',
	},
	buttonCancel: {
		id: 'external-collab-ui.satisfaction-survey.button.cancel',
		defaultMessage: 'Cancel',
		description: 'Button to close the survey without submitting',
	},
});

export type ExternalCollabSatisfactionSurveyProps = {
	/** Whether the user responding to the survey is a guest or not */
	isGuest?: boolean;

	/** Callback invoked when the survey is ready to be closed (has been submitted or dismissed). */
	onDone: () => void;

	/** Callback invoked when the survey has been submitted.  Note this is different than "onDone()",
	 * as the survey dialog actually stays open after the submitted to show a "thank you" message. */
	onSubmitted?: () => void;

	/** Where within the application this survey was triggered from. */
	source: string;
};

export const ExternalCollabSatisfactionSurvey: FC<ExternalCollabSatisfactionSurveyProps> = ({
	isGuest = false,
	onDone,
	onSubmitted,
	source,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isCompleted, setCompleted] = useState<boolean>(false);
	const [canContactUser, setCanContactUser] = useState<boolean>(false);

	// Note that the SatisfactionSurveyComponent can call "onInteraction()" several times when being
	// dismissed: once when the survey is closed (either when it is dismissed without submission or
	// when the "Thanks" is closed after submission), and once when it is unmounted (intended as a
	// fallback). But we only want to call onDone() once, so we use a Ref to keep track of that since
	// a state variable would cause an extra unwanted render when it changes (which refs do not cause)
	const onDoneFired = useRef<boolean>(false);

	const intl = useIntl();

	const handleSurveyInteraction = ({
		dismissed,
		feedback,
		reaction,
		submitted,
	}: OnInteractionData) => {
		if (dismissed && onDoneFired.current === false) {
			onDoneFired.current = true;
			onDone();
		} else if (submitted) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'submitted',
					actionSubject: 'survey',
					actionSubjectId: 'externalCollabCsatSurvey',
					source,
					attributes: {
						canContact: canContactUser,
						reaction: reaction.value,
						feedback,
						isGuest,
					},
				},
			}).fire();
			onSubmitted && onSubmitted();
			setCompleted(true);
		}
	};

	const canContactUserCheckbox = (
		<AkCheckbox
			isChecked={canContactUser}
			label={<FormattedMessage {...i18n.canContact} />}
			name="can-contact-checkbox"
			onChange={(event) => {
				setCanContactUser(event.currentTarget.checked);
			}}
		/>
	);

	const questionGood = <FormattedMessage {...i18n.questionGood} />;
	const questionNeutral = <FormattedMessage {...i18n.questionNeutral} />;
	const questionBad = <FormattedMessage {...i18n.questionBad} />;
	const reactionQuestions = [questionBad, questionBad, questionNeutral, questionGood, questionGood];

	const reactionTitles: JSX.Element[] = [
		<FormattedMessage {...i18n.reactionTerrible} key={i18n.reactionTerrible.id} />,
		<FormattedMessage {...i18n.reactionBad} key={i18n.reactionBad.id} />,
		<FormattedMessage {...i18n.reactionOkay} key={i18n.reactionOkay.id} />,
		<FormattedMessage {...i18n.reactionGreat} key={i18n.reactionGreat.id} />,
		<FormattedMessage {...i18n.reactionAmazing} key={i18n.reactionAmazing.id} />,
	];

	return (
		<SatisfactionSurveyComponent
			title={<FormattedMessage {...i18n.title} />}
			description={<FormattedMessage {...i18n.description} />}
			finalTitle={<FormattedMessage {...i18n.finalTitle} />}
			finalDescription={<FormattedMessage {...i18n.finalDescription} />}
			reactionTitles={reactionTitles}
			formQuestions={reactionQuestions}
			submitButtonText={<FormattedMessage {...i18n.buttonSubmit} />}
			// closeButtonText needs to be a string because it is used as an aria-label too
			closeButtonText={intl.formatMessage(i18n.buttonCancel)}
			onInteraction={handleSurveyInteraction}
			isOnFinalStage={isCompleted}
			customFields={canContactUserCheckbox}
		/>
	);
};
